import log from "loglevel";
import update from "immutability-helper";
import { isType, Action } from "typescript-fsa";
import { Map } from "immutable";
import { saveAs } from "file-saver";

import { logout } from "@auth/actions";
import { ApiRequestProgress, ApiRequestResponse, ApiRequestErrorPair } from "../models/actions";
import { ApiInternalState as State } from "../models/state";
import { ApiRequest } from "../requests";
import * as actions from "../actions";

const getInitialState = () => ({
    requests: Map<string, ApiRequest>(),
    uriToRequests: Map<string, string[]>(),
} as State);

function api(state: State = getInitialState(), action: actions.ApiAction): State {
    // State should never be null...
    if (state === null) {
        log.warn("api state setting to null, resetting to initialState.");
        state = {} as State;
        state = update(state, { $set: getInitialState() });
    }

    if (isType(action, actions.apiRequest)) {
        // Api request
        return apiRequest(state, action);
    }
    else if (isType(action, actions.apiRequestProgress)) {
        // Api request progress
        return apiRequestProgress(state, action);
    }
    else if (isType(action, actions.apiResponseSuccess)) {
        // Successful response
        return apiResponseSuccess(state, action);
    }
    else if (isType(action, actions.apiResponseFailure)) {
        if (actions.isFailedResponseUnauthenticatedNotLogin(action)) {
            return update(state, { $set: getInitialState() });
        }
        // Failed response
        return apiResponseFailure(state, action);
    }
    else if (isType(action, actions.apiRequestExpire)) {
        // Expire request
        return apiRequestExpire(state, action);
    }
    else if (isType(action, logout)) {
        // Clear the state on logout.
        return update(state, { $set: getInitialState() });
    }
    // Otherwise we do nothing
    return state;
}

function apiRequest(state: State, action: Action<ApiRequest>): State {
    // Update the state - set to in progress
    const request = action.payload.setInProgress();
    const newState = {
        requests: state.requests.set(request.uuid, request),
    } as State;
    return update(state, { $merge: newState });
}

function apiRequestProgress(state: State, { payload }: Action<ApiRequestProgress>): State {
    // Update the state - set to in progress
    const uuid = payload.request.uuid;
    let request = state.requests.get(uuid);
    request = request.setInProgress();
    request = request.setProgress(payload.progress * 0.95);
    const newState = {
        requests: state.requests.set(uuid, request),
    } as State;
    return update(state, { $merge: newState });
}

function apiResponseSuccess(state: State, { payload }: Action<ApiRequestResponse>): State {
    const uuid = payload.request.uuid;
    let request = state.requests.get(uuid);
    // If the request is a download, download it at this point.
    if (request.options.file != null && request.options.file &&
        payload.blob != null && payload.filename != null
    ) {
        saveAs(payload.blob, payload.filename);
    }
    // Update the state - set to finished - success.
    request = request.setFinishedSucceeded(payload.response);
    // Also add the UUID to the uriToRequests map.
    const endpoint = request.getEndpoint();
    let uriToRequests = state.uriToRequests;
    if (!state.uriToRequests.has(endpoint)) {
        uriToRequests = uriToRequests.set(endpoint, []);
    }
    const arr = uriToRequests.get(endpoint);
    arr.push(uuid);

    const newState = {
        requests: state.requests.set(request.uuid, request),
        uriToRequests: uriToRequests.set(endpoint, arr),
    } as State;
    return update(state, { $merge: newState });
}

function apiResponseFailure(state: State, { payload }: Action<ApiRequestErrorPair>): State {
    const uuid = payload.request.uuid;
    let request = state.requests.get(uuid);
    // Update the state - set to finished - success.
    request = request.setFinishedFailed(payload.error);
    const newState = {
        requests: state.requests.set(request.uuid, request),
    } as State;
    return update(state, { $merge: newState });
}

function apiRequestExpire(state: State, { payload }: Action<ApiRequest>): State {
    // Remove the request
    const uuid = payload.uuid;
    const newState = {
        requests: state.requests.delete(uuid),
    } as State;
    return update(state, { $merge: newState });
}

export {
    api,
    getInitialState,
};
